import type { GameDefinition, Outcome, Rng, Seat, Viewer } from '@campfire/engine';
import { shuffle, standardDeck, SUITS, type Card, type Suit } from '@campfire/cards';

export interface CrazyEightsOptions {
  /** Points to win the match; null = a single round. */
  readonly target: number | null;
  /** 'one': draw one card, play it if it fits, else the turn passes. 'until': keep drawing until a card fits. */
  readonly drawRule: 'one' | 'until';
}

export type CrazyEightsEvent =
  | { readonly kind: 'play'; readonly seat: Seat; readonly card: Card; readonly suit: Suit | null }
  /** `cards` are the drawn cards; only the drawer sees them. */
  | { readonly kind: 'draw'; readonly seat: Seat; readonly count: number; readonly cards: readonly Card[] | null; readonly playable: boolean }
  | { readonly kind: 'pass'; readonly seat: Seat }
  | { readonly kind: 'round'; readonly winner: Seat; readonly points: number; readonly round: number; readonly blocked: boolean };

export interface CrazyEightsState {
  readonly options: CrazyEightsOptions;
  readonly hands: readonly (readonly Card[])[];
  /** Index 0 is the top. */
  readonly stock: readonly Card[];
  /** Index 0 is the face-up top card. */
  readonly discard: readonly Card[];
  /** Suit to follow; null after a starting 8 (anything goes). */
  readonly suit: Suit | null;
  readonly turn: Seat;
  /** A just-drawn card that fits: play it, or (draw-one rule) keep it and pass. */
  readonly pending: string | null;
  /** Consecutive passes with nothing left to draw; a full lap blocks the round. */
  readonly stuck: number;
  readonly dealer: Seat;
  readonly round: number;
  readonly scores: readonly number[];
  readonly last: CrazyEightsEvent | null;
  readonly result: Outcome | null;
}

export type CrazyEightsAction =
  | { readonly type: 'play'; readonly card: string; readonly suit?: Suit }
  | { readonly type: 'draw' }
  | { readonly type: 'pass' };

export interface CrazyEightsView {
  readonly you: Seat | null;
  readonly hand: readonly Card[] | null;
  /** Ids in your hand you may play right now. */
  readonly playable: readonly string[];
  readonly handCounts: readonly number[];
  readonly top: Card;
  readonly suit: Suit | null;
  readonly turn: Seat;
  readonly stockCount: number;
  /** True when drawing is possible (stock or reshuffle). */
  readonly canDraw: boolean;
  readonly pending: string | null;
  readonly round: number;
  readonly dealer: Seat;
  readonly scores: readonly number[];
  readonly target: number | null;
  readonly drawRule: 'one' | 'until';
  readonly last: CrazyEightsEvent | null;
  readonly result: Outcome | null;
}

/** 8 = 50, J/Q/K = 10, ace 1, pips face value (FR-EIGHTS-06). */
export function eightsPoints(c: Card): number {
  if (c.rank === '8') return 50;
  if (c.rank === 'A') return 1;
  if (c.rank === 'J' || c.rank === 'Q' || c.rank === 'K') return 10;
  return Number(c.rank);
}

const top = (s: CrazyEightsState) => s.discard[0] as Card;
const next = (s: CrazyEightsState, seat: Seat) => (seat + 1) % s.hands.length;
const handSize = (seats: number) => (seats >= 5 ? 5 : 7);
const handPoints = (h: readonly Card[]) => h.reduce((p, c) => p + eightsPoints(c), 0);

export function fits(s: CrazyEightsState, c: Card): boolean {
  return c.rank === '8' || s.suit === null || c.suit === s.suit || c.rank === top(s).rank;
}

const canDraw = (s: CrazyEightsState) => s.stock.length > 0 || s.discard.length > 1;

function deal(options: CrazyEightsOptions, seats: number, dealer: Seat, round: number, scores: readonly number[], rng: Rng): CrazyEightsState {
  const deck = shuffle(standardDeck(), rng);
  const n = handSize(seats);
  const hands = Array.from({ length: seats }, (_, i) => deck.slice(i * n, i * n + n));
  const starter = deck[seats * n] as Card;
  return {
    options,
    hands,
    stock: deck.slice(seats * n + 1),
    discard: [starter],
    // A starting 8 stays put and the first player may play anything.
    suit: starter.rank === '8' ? null : starter.suit,
    turn: (dealer + 1) % seats,
    pending: null,
    stuck: 0,
    dealer,
    round,
    scores,
    last: null,
    result: null,
  };
}

/** Draw one card, reshuffling the discard pile (all but its top) when the stock runs out. */
function drawOne(s: CrazyEightsState, seat: Seat, rng: Rng): { state: CrazyEightsState; card: Card } {
  let stock = s.stock;
  let discard = s.discard;
  if (stock.length === 0) {
    stock = shuffle(discard.slice(1), rng);
    discard = discard.slice(0, 1);
  }
  const card = stock[0] as Card;
  const hands = s.hands.map((h, i) => (i === seat ? [...h, card] : h));
  return { state: { ...s, hands, stock: stock.slice(1), discard }, card };
}

function endRound(s: CrazyEightsState, winner: Seat, blocked: boolean, rng: Rng): CrazyEightsState {
  const points = s.hands.reduce((sum, h, i) => (i === winner ? sum : sum + handPoints(h)), 0);
  const scores = s.scores.map((v, i) => (i === winner ? v + points : v));
  const target = s.options.target;
  const done = target === null || (scores[winner] ?? 0) >= target;
  const last: CrazyEightsEvent = { kind: 'round', winner, points, round: s.round, blocked };
  if (done) {
    return { ...s, scores, last, result: { winners: [winner], draw: false, reason: blocked ? 'blocked' : target === null ? 'out' : 'target', scores: { ...scores } } };
  }
  return { ...deal(s.options, s.hands.length, (s.dealer + 1) % s.hands.length, s.round + 1, scores, rng), last };
}

/** Nobody can play or draw: the lowest hand wins the round (ties: first after the dealer). */
function blockedWinner(s: CrazyEightsState): Seat {
  let best = (s.dealer + 1) % s.hands.length;
  for (let k = 1; k < s.hands.length; k++) {
    const seat = (s.dealer + 1 + k) % s.hands.length;
    if (handPoints(s.hands[seat] ?? []) < handPoints(s.hands[best] ?? [])) best = seat;
  }
  return best;
}

export const crazyEights: GameDefinition<CrazyEightsState, CrazyEightsAction, CrazyEightsView, CrazyEightsOptions> = {
  id: 'crazy-eights',
  name: 'Crazy Eights',
  rules: [
    'Be first to get rid of all your cards. A turn is: play a card that matches the top discard by suit or by rank, or play an 8.',
    'Each player gets 7 cards (5 at a table of 5 or more). One card is turned up to start the discard pile.',
    'An 8 is wild: play it any time and name the suit that must be followed next. If the starting card is an 8, the first player may play anything.',
    'No other cards do anything special: no skips, reverses or draw cards.',
    'If you can play, you must: the app will not let you draw while you hold a playable card.',
    'Setting: When you can’t play. Draw one, then pass (if the drawn card fits you may play it, or keep it and pass) or draw until you can play.',
    'When the stock runs out the discards (except the top card) are reshuffled. If nobody can play or draw for a full lap, the round is blocked and the lowest hand wins.',
    'Scoring: the winner scores the cards left in everyone else’s hands (8 = 50, J/Q/K = 10, ace = 1, other cards face value).',
    'Setting: Game length. One round, or play on until someone reaches 100 or 200 points.',
  ],
  minSeats: 2,
  maxSeats: 7,
  defaultOptions: { target: null, drawRule: 'one' },
  optionSpecs: [
    {
      key: 'target',
      label: 'Game length',
      help: 'The first player out scores the cards left in everyone else’s hands (8 = 50, faces 10, ace 1).',
      choices: [
        { value: null, label: 'One round' },
        { value: 100, label: 'First to 100 points' },
        { value: 200, label: 'First to 200 points' },
      ],
    },
    {
      key: 'drawRule',
      label: 'When you can’t play',
      choices: [
        { value: 'one', label: 'Draw one, then pass' },
        { value: 'until', label: 'Draw until you can play' },
      ],
    },
  ],

  setup({ seats, options, rng }) {
    return deal(options, seats, 0, 1, Array.from({ length: seats }, () => 0), rng);
  },

  activeSeats: (s) => (s.result ? [] : [s.turn]),

  validate(s, a, seat) {
    if (seat !== s.turn) return { ok: false, reason: 'not your turn' };
    const hand = s.hands[seat] ?? [];
    const anyFits = hand.some((c) => fits(s, c));
    if (a.type === 'draw') {
      if (s.pending) return { ok: false, reason: 'play the card you drew or keep it' };
      if (anyFits) return { ok: false, reason: 'you have a card you can play' };
      return canDraw(s) ? { ok: true } : { ok: false, reason: 'nothing left to draw — pass' };
    }
    if (a.type === 'pass') {
      if (s.pending) return s.options.drawRule === 'one' ? { ok: true } : { ok: false, reason: 'play the card you drew' };
      return !anyFits && !canDraw(s) ? { ok: true } : { ok: false, reason: anyFits ? 'you have a card you can play' : 'draw a card first' };
    }
    if (a.type !== 'play') return { ok: false, reason: 'unknown action' };
    const card = hand.find((c) => c.id === a.card);
    if (!card) return { ok: false, reason: 'you do not hold that card' };
    if (s.pending && card.id !== s.pending) return { ok: false, reason: 'you may only play the card you just drew' };
    if (!fits(s, card)) return { ok: false, reason: 'match the suit or rank, or play an 8' };
    if (card.rank === '8' && !SUITS.includes(a.suit as Suit)) return { ok: false, reason: 'name a suit for your 8' };
    return { ok: true };
  },

  apply(s, a, { rng }) {
    const seat = s.turn;
    if (a.type === 'draw') {
      let st = s;
      const drawn: Card[] = [];
      for (;;) {
        const res = drawOne(st, seat, rng);
        st = res.state;
        drawn.push(res.card);
        if (fits(st, res.card) || s.options.drawRule === 'one' || !canDraw(st)) break;
      }
      const card = drawn[drawn.length - 1] as Card;
      const playable = fits(st, card);
      const last: CrazyEightsEvent = { kind: 'draw', seat, count: drawn.length, cards: drawn, playable };
      return playable ? { ...st, pending: card.id, stuck: 0, last } : { ...st, turn: next(st, seat), stuck: 0, last };
    }
    if (a.type === 'pass') {
      const stuck = s.pending ? 0 : s.stuck + 1;
      const st: CrazyEightsState = { ...s, pending: null, stuck, turn: next(s, seat), last: { kind: 'pass', seat } };
      return stuck >= s.hands.length ? endRound(st, blockedWinner(st), true, rng) : st;
    }
    const card = (s.hands[seat] as readonly Card[]).find((c) => c.id === a.card) as Card;
    const suit = card.rank === '8' ? (a.suit as Suit) : card.suit;
    const hand = (s.hands[seat] as readonly Card[]).filter((c) => c.id !== card.id);
    const st: CrazyEightsState = {
      ...s,
      hands: s.hands.map((h, i) => (i === seat ? hand : h)),
      discard: [card, ...s.discard],
      suit,
      pending: null,
      stuck: 0,
      turn: next(s, seat),
      last: { kind: 'play', seat, card, suit: card.rank === '8' ? suit : null },
    };
    return hand.length === 0 ? endRound(st, seat, false, rng) : st;
  },

  view(s, viewer: Viewer) {
    const you = viewer.kind === 'seat' ? viewer.seat : null;
    const hand = you === null ? null : (s.hands[you] ?? null);
    const mine = you !== null && you === s.turn && !s.result;
    const playable = mine && hand ? hand.filter((c) => (s.pending ? c.id === s.pending : true) && fits(s, c)).map((c) => c.id) : [];
    const last = s.last;
    const shownLast = last?.kind === 'draw' && last.seat !== you ? { ...last, cards: null } : last;
    return {
      you,
      hand,
      playable,
      handCounts: s.hands.map((h) => h.length),
      top: top(s),
      suit: s.suit,
      turn: s.turn,
      stockCount: s.stock.length,
      canDraw: canDraw(s),
      pending: mine ? s.pending : null,
      round: s.round,
      dealer: s.dealer,
      scores: s.scores,
      target: s.options.target,
      drawRule: s.options.drawRule,
      last: shownLast,
      result: s.result,
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    if (s.result || seat !== s.turn) return [];
    const out: CrazyEightsAction[] = [];
    for (const c of s.hands[seat] ?? []) {
      if (s.pending && c.id !== s.pending) continue;
      if (!fits(s, c)) continue;
      if (c.rank === '8') for (const suit of SUITS) out.push({ type: 'play', card: c.id, suit });
      else out.push({ type: 'play', card: c.id });
    }
    for (const a of [{ type: 'draw' }, { type: 'pass' }] as const) {
      if (crazyEights.validate(s, a, seat).ok) out.push(a);
    }
    return out;
  },
};
